
import { defineComponent, PropType, computed } from "vue";
import { GroupedAlert, MessageType } from "@/stores/telegramAlerts";
import AlertDetails from "@/components/alerts/AlertDetails.vue";
import {
  formatDate,
  cleanAlertText,
  getAlertSummary,
} from "@/components/alerts/utils/alertFormatters";
import {
  getResultLabel,
  getResultBadgeClass,
} from "@/components/alerts/utils/alertStatus";
import { getStakeColor } from "@/components/alerts/utils/stakeColors";

export default defineComponent({
  name: "SportsAlertRow",
  components: {
    AlertDetails,
  },
  props: {
    alert: {
      type: Object as PropType<GroupedAlert>,
      required: true,
    },
    rowNumber: {
      type: Number,
      required: true,
    },
    sortedStakes: {
      type: Array as PropType<string[]>,
      required: true,
    },
    expandedAlertId: {
      type: Object as PropType<Set<string>>,
      required: true,
    },
    editingAlertId: {
      type: String as PropType<string | null>,
      default: null,
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
    isReprocessing: {
      type: Object as PropType<Record<number, boolean>>,
      default: () => ({}),
    },
  },
  emits: [
    "toggle-expand",
    "start-edit",
    "cancel-edit",
    "save-edit",
    "reprocess-message",
  ],
  setup(props, { emit }) {
    const isExpanded = computed(() =>
      props.expandedAlertId.has(props.alert.alertId)
    );
    const isEditing = computed(
      () => props.editingAlertId === props.alert.alertId
    );

    const toggleExpand = () => {
      emit("toggle-expand", props.alert.alertId);
    };

    const hasEditedMessages = (alert: GroupedAlert) => {
      return alert.relatedMessages.some(
        (msg) =>
          msg.messageType === MessageType.UPDATE ||
          msg.messageType === MessageType.ADJUSTMENT
      );
    };

    const hasDeletedMessages = (alert: GroupedAlert) => {
      return alert.relatedMessages.some(
        (msg) => msg.messageType === MessageType.MANUAL_CLOSE
      );
    };

    // Get stake badge color and style
    const getStakeBadgeStyle = (stake: string) => {
      const color = getStakeColor(stake, props.sortedStakes, "solid");
      return {
        backgroundColor: color,
        color: "#fff",
        border: "none",
      };
    };

    return {
      isExpanded,
      isEditing,
      toggleExpand,
      formatDate,
      cleanAlertText,
      getAlertSummary,
      getResultLabel,
      getResultBadgeClass,
      hasEditedMessages,
      hasDeletedMessages,
      getStakeBadgeStyle,
    };
  },
});
